import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Sparkles } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { SourceBadge } from "@/components/SourceBadge";
import { GameLog } from "@/components/GameLog";
import { ExportJsonButton } from "@/components/ExportJsonButton";
import { PlayerFormDialog } from "@/pages/admin/PlayerFormDialog";
import { useAuth } from "@/context/AuthContext";
import { SPORTS, XG_KEYS, fmtDate, fmtHeight, fmtStat, statLabel } from "@/constants/sports";
import { XgPanel } from "@/components/XgPanel";
import { VideoMatches } from "@/components/VideoMatches";
import { AttributesPanel, hasAttributes } from "@/components/AttributesPanel";
import { api, photoSrc } from "@/lib/api";

const Section = ({ title, badge, children, testId }) => (
  <section data-testid={testId} className="rounded-xl border border-slate-800/80 bg-[#131927] p-6 sm:p-8">
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">{title}</h2>
      {badge}
    </div>
    {children}
  </section>
);

const Tile = ({ label, value, testId, big }) => (
  <div data-testid={testId} className="bg-[#131927] p-4 outline outline-1 outline-slate-800 sm:p-5">
    <div className="eyebrow">{label}</div>
    <div className={`mt-2 font-data font-bold text-white ${big ? "text-3xl" : "text-lg"}`}>{value ?? "—"}</div>
  </div>
);

const RosterSource = ({ p }) => (
  <div data-testid="roster-source" className="flex flex-wrap items-center gap-2">
    {p.on_current_roster === false && <span data-testid="not-on-roster-badge" className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 font-data text-[11px] uppercase text-amber-300">Not on current roster</span>}
    <SourceBadge source="school_roster" updatedAt={p.roster_collected_at} testId="roster-source-badge" />
    {p.roster_url && <a href={p.roster_url} target="_blank" rel="noopener noreferrer" data-testid="roster-source-link" className="font-data text-[11px] text-teal-300 underline-offset-4 hover:underline">View roster</a>}
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-slate-500">{text}</p>;

export default function PlayerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => api.get(`/players/${id}`).then((r) => setP(r.data)).catch(() => setError(true)), [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return (<div className="min-h-screen"><AppHeader /><p data-testid="player-not-found" className="p-16 text-center font-display text-3xl uppercase">Player not found</p></div>);
  if (!p) return <div className="min-h-screen"><AppHeader /></div>;

  const sport = SPORTS[p.sport];
  const statKeys = [...sport.stats.map((s) => s.key).filter((k) => k in p.stats), ...Object.keys(p.stats).filter((k) => !sport.stats.some((s) => s.key === k) && !XG_KEYS.includes(k))];
  const bio = [["Height", fmtHeight(p.height_in)], ["Weight", p.weight_lb ? `${p.weight_lb} lb` : null], ["Age", p.age], ...(p.sport === "beach" ? [] : [["Position", p.position]]), ["Team", p.team], ["Class", p.class_year], ["Hometown", p.hometown],
    ...(sport.rosterOnly ? [...(p.sport === "softball" ? [["Bats/Throws", p.bats_throws]] : []),
      ...(p.sport === "rugby" ? [["Unit", (p.position_group !== "Unknown" && p.position_group) || null], ["Division", p.division]] : []), ["High school", p.high_school], ["Previous school", p.previous_school], ["Jersey", p.jersey_number && `#${p.jersey_number}`]] : [])];

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-4 pb-24 pt-8 sm:px-8">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/players" data-testid="back-to-search-link" className="eyebrow inline-flex items-center gap-2 transition-colors hover:!text-white">
            <ArrowLeft className="h-4 w-4" /> Back to database
          </Link>
          {user?.role === "admin" && (
            <div className="flex gap-2">
            <ExportJsonButton player={p} testId="profile-export-json-button" />
            <button data-testid="profile-edit-player-button" onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm transition-colors hover:border-[#FF2D78]">
              <Pencil className="h-4 w-4" /> Edit player
            </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="rise relative overflow-hidden rounded-xl border border-slate-800 lg:col-span-4 lg:row-span-2">
            <div className="relative aspect-[3/4] lg:aspect-auto lg:h-full lg:min-h-[640px]">
              {photoSrc(p) ? <img data-testid="profile-photo" src={photoSrc(p)} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
                : <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${p.team_color || "#1A2336"} 0%, #0B0F19 75%)` }}>
                    {p.team_logo ? <img data-testid="profile-team-logo-hero" src={p.team_logo} alt="" className="w-1/2 -translate-y-12 object-contain opacity-30" />
                      : <span className="font-display text-9xl font-black text-white/10">{p.name[0]}</span>}
                  </div>}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
              <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: sport.accent }} />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                <p className="font-data text-xs uppercase tracking-[0.2em]" style={{ color: sport.accent }}>
                  {[sport.label, p.position, p.jersey_number && `#${p.jersey_number}`].filter(Boolean).join(" · ")}
                </p>
                <h1 data-testid="profile-player-name" className="mt-2 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl">{p.name}</h1>
                <p data-testid="profile-player-team" className="mt-2 flex flex-wrap items-center gap-2 text-lg text-slate-300">
                  {p.team_logo && <img src={p.team_logo} alt="" className="h-6 w-6 object-contain" />}{p.team}
                  <Link data-testid="profile-find-like-her" to={`/prototypes?like=${p.id}`}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#FF2D78]/60 px-2.5 py-0.5 font-data text-[10px] uppercase tracking-wider text-[#FF2D78] transition-colors duration-200 hover:bg-[#FF2D78]/15">
                    <Sparkles className="h-3 w-3" /> Find players like her
                  </Link>
                  {p.linked_card && (
                    <Link data-testid="profile-linked-card" to={`/players/${p.linked_card.id}`}
                      className="whitespace-nowrap rounded-full border px-2.5 py-0.5 font-data text-[10px] uppercase tracking-wider transition-colors duration-200 hover:bg-white/10"
                      style={{ borderColor: SPORTS[p.linked_card.sport].accent, color: SPORTS[p.linked_card.sport].accent }}>
                      Also plays {p.linked_card.sport === "beach" ? "beach" : "indoor"} volleyball →
                    </Link>
                  )}
                </p>
                {p.conference && <p data-testid="profile-player-conference" className="font-data text-xs uppercase tracking-widest text-slate-500">{p.conference}</p>}
              </div>
            </div>
          </div>
          <div className="min-w-0 space-y-6 lg:col-span-8">
            <Section title="Bio" testId="profile-bio-section"
              badge={sport.rosterOnly && <RosterSource p={p} />}>
              <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-slate-800 sm:grid-cols-3">
                {bio.map(([l, v]) => <Tile key={l} label={l} value={v} testId={`bio-${l.toLowerCase()}`} />)}
              </div>
            </Section>
            <Section title={`${p.season} Season stats`} testId="profile-stats-section"
              badge={!sport.rosterOnly && <SourceBadge source={p.stats_source} updatedAt={p.stats_updated_at} testId="stats-source-badge" />}>
              {statKeys.length ? (
                <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-slate-800 sm:grid-cols-4">
                  {statKeys.map((k) => <Tile key={k} big label={statLabel(p.sport, k)} value={fmtStat(k, p.stats[k])} testId={`stat-${k}`} />)}
                </div>
              ) : <Empty text={sport.rosterOnly ? "Not available yet." : "No season stats yet."} />}
            </Section>
            {hasAttributes(p.sport, p.attributes_by_season?.[sport.attrSeason]) && (
              <Section title="Attributes" testId="profile-attributes-section"
                badge={<span data-testid="attributes-season-label" className="rounded-full border border-slate-600 px-2.5 py-0.5 font-data text-[11px] uppercase tracking-wider text-slate-300">{sport.attrSeason} season</span>}>
                <AttributesPanel attrs={p.attributes_by_season[sport.attrSeason]} sport={p.sport} />
              </Section>
            )}
            {p.sport === "soccer" && p.stats.xg !== undefined && (
              <Section title="Box Score xG" testId="profile-xg-section"
                badge={<SourceBadge source={p.stats_source} updatedAt={p.stats_updated_at} testId="xg-source-badge" />}>
                <XgPanel stats={p.stats} position={p.position} />
              </Section>
            )}
            {p.sport === "soccer" && p.video_matches?.length > 0 && (
              <Section title="Match video analysis" testId="profile-video-section">
                <VideoMatches matches={p.video_matches} />
              </Section>
            )}
            {!sport.rosterOnly && (
              <Section title="Game log" testId="profile-gamelog-section">
                <GameLog playerId={p.id} sport={p.sport} />
              </Section>
            )}
            <Section title="Athletic testing" testId="profile-testing-section"
              badge={<SourceBadge source={p.testing_source} updatedAt={p.testing_updated_at} testId="testing-source-badge" />}>
              {p.testing.length ? (
                <div className="divide-y divide-slate-800 rounded-lg border border-slate-800">
                  {p.testing.map((t, i) => (
                    <div key={i} data-testid={`testing-row-${i}`} className="flex items-center justify-between gap-4 px-5 py-4">
                      <div>
                        <div className="font-medium text-white">{t.test}</div>
                        <div className="font-data text-[11px] text-slate-500">Tested {fmtDate(t.date)}</div>
                      </div>
                      <div className="font-data text-2xl font-bold text-white">{t.value}<span className="ml-1 text-sm text-slate-400">{t.unit}</span></div>
                    </div>
                  ))}
                </div>
              ) : <Empty text={sport.rosterOnly ? "Not available yet." : "No testing results yet."} />}
            </Section>
          </div>
        </div>
      </main>
      {editing && <PlayerFormDialog player={p} open onOpenChange={setEditing} onSaved={load} />}
    </div>
  );
}
